import { useCallback, useEffect, useRef, useState } from 'react';
import {
  markHiringHistoryInterest,
  unmarkHiringHistoryInterest,
} from 'services/actions/ai_management_hiring_history';
import {
  PossibleCompetitorsItemResponse,
  PossibleCompetitorsOrigin,
  PossibleCompetitorsResponse,
  listPossibleCompetitors,
  peekPossibleCompetitors,
} from 'services/actions/ai_management_possible_competitors';

export interface PossibleCompetitorsItem {
  key: string;
  label: string;
  catalog: string | null;
  description: string | null;
  nationwide: boolean;
  interested: boolean;
  companies: number;
  biddings: number;
  averagePrice: number | null;
}

interface State {
  items: PossibleCompetitorsItem[];
  total: number;
  state: string | null;
  origin: PossibleCompetitorsOrigin | null;
  loading: boolean;
  error: boolean;
}

const PER_PAGE = 10;
export const DEFAULT_PERIOD = 3;

const toItem = (item: PossibleCompetitorsItemResponse): PossibleCompetitorsItem => ({
  key: item.key,
  label: item.label,
  catalog: item.catalogo ?? null,
  description: item.descricao ?? null,
  nationwide: item.escopo === 'brasil',
  interested: item.interesse,
  companies: item.empresas,
  biddings: item.licitacoes,
  averagePrice: item.media_preco,
});

const fromResponse = (data: PossibleCompetitorsResponse): State => ({
  items: data.items.map(toItem),
  total: data.total,
  state: data.estado ?? null,
  origin: data.origem ?? null,
  loading: false,
  error: false,
});

const initialState: State = {
  items: [],
  total: 0,
  state: null,
  origin: null,
  loading: true,
  error: false,
};

export function usePossibleCompetitors(biddingId: number | null) {
  const [state, setState] = useState<State>(() => {
    const hit = biddingId
      ? peekPossibleCompetitors(biddingId, {
          page: 1,
          perPage: PER_PAGE,
          search: '',
          period: DEFAULT_PERIOD,
        })
      : undefined;
    return hit ? fromResponse(hit.data) : initialState;
  });
  const [page, setPage] = useState(1);
  const [period, setPeriodState] = useState(DEFAULT_PERIOD);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const silentRef = useRef(false);

  useEffect(() => {
    if (!biddingId) return undefined;

    let active = true;
    const silent = silentRef.current;
    silentRef.current = false;
    const params = { page, perPage: PER_PAGE, search: appliedSearch, period };

    const hit = peekPossibleCompetitors(biddingId, params);
    if (hit) {
      setState(fromResponse(hit.data));
      return undefined;
    }
    if (!silent) setState((current) => ({ ...current, loading: true, error: false }));

    listPossibleCompetitors(biddingId, params)
      .then(({ data }) => {
        if (active) setState(fromResponse(data));
      })
      .catch(() => {
        if (!active) return;
        setState({ ...initialState, loading: false, error: true });
      });

    return () => {
      active = false;
    };
  }, [biddingId, page, appliedSearch, period, refreshKey]);

  const submitSearch = useCallback(() => {
    setPage(1);
    setAppliedSearch(search.trim());
  }, [search]);

  const clearSearch = useCallback(() => {
    setSearch('');
    setPage(1);
    setAppliedSearch('');
  }, []);

  const setPeriod = useCallback((value: number) => {
    setPage(1);
    setPeriodState(value);
  }, []);

  const toggleInterest = useCallback(
    async (keys: string[], interested: boolean) => {
      if (!biddingId || keys.length === 0) return;

      const antes = state.items;
      setState((current) => ({
        ...current,
        items: current.items.map((item) =>
          keys.includes(item.key) ? { ...item, interested } : item,
        ),
      }));

      try {
        if (interested) {
          const marcados = antes
            .filter((item) => keys.includes(item.key))
            .map((item) => ({ key: item.key, label: item.label }));
          await markHiringHistoryInterest(biddingId, marcados);
        } else {
          await unmarkHiringHistoryInterest(biddingId, keys);
        }
        silentRef.current = true;
        setRefreshKey((current) => current + 1);
      } catch {
        setState((current) => ({ ...current, items: antes }));
      }
    },
    [biddingId, state.items],
  );

  return {
    ...state,
    page,
    perPage: PER_PAGE,
    period,
    search,
    appliedSearch,
    setSearch,
    submitSearch,
    clearSearch,
    setPage,
    setPeriod,
    toggleInterest,
  };
}
